import { useEffect, useRef, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { isUnlocked, type DisclosureStage } from '@kilat/shared';

import { useDisclosureStage } from '../lib/use-disclosure.js';
import { useI18n } from '../lib/use-i18n.js';
import { env } from '../lib/env.js';
import { useIsCompact } from './use-is-compact.js';
import { isFloorRole, useMyRole } from '../lib/use-my-role.js';
import { InboxBell } from './inbox-bell.js';
import { AskKilat } from './ask-kilat.js';
import {
  BellIcon,
  BuildingIcon,
  CarIcon,
  GiftIcon,
  ReceiptIcon,
  ShieldIcon,
  StarIcon,
  UserIcon,
} from '@kilat/ui';
import { ThemeToggle } from '@kilat/ui';

import { LocaleSwitcher } from './locale-switcher.js';

/**
 * Copy for the appearance control, shared by the two nav bars below.
 *
 * The desktop and compact bars are separate returns in this file; a literal in
 * each is how the same control ends up with two different labels after someone
 * edits one of them.
 */
function appearanceLabels(t: (key: string) => string) {
  return {
    group: t('common.appearance.group'),
    system: t('common.appearance.system'),
    light: t('common.appearance.light'),
    dark: t('common.appearance.dark'),
  };
}

/**
 * Dashboard cross-page navigation.
 *
 * REBUILT 2026-08-20 after the owner reviewed the running product: the nav was
 * twenty undifferentiated pills wrapping onto two rows, which is a menu dump
 * rather than navigation — every destination shouting equally, so none of them
 * reads. Worse, and the reason this became urgent: **the two screens an
 * operator uses all day — the queue board and the till — were not in it at
 * all.** `/pos` was reachable only by typing the URL.
 *
 * Shape now:
 *   - FOUR primary destinations as a segmented control: Queue, Till, Customers,
 *     Reports. These are the daily job. The active one is filled, not merely
 *     outlined, so where-am-I is answerable at a glance from across a counter.
 *   - Everything else lives in grouped menus — Loyalty, Marketing, Setup, and
 *     Platform for super-admins. Grouping is by what an operator came to do,
 *     not by which sprint shipped it.
 *   - ≤ 720px collapses to the existing hamburger drawer, now with the same
 *     groups as headings rather than one flat list.
 *   - REBUILT AGAIN 2026-10-16 into two tiers (AppNavUtilities in the top row,
 *     AppNavLinks below it) after the owner saw, on a tablet, the single
 *     wrapping row strand the assistant button alone on an empty line.
 *
 * Every link is a real 44px tap target (`--touch-target`) — plain `<a>`s inside
 * `<nav>` don't inherit the global button min-height, so it's set explicitly.
 */

interface NavLinkDef {
  readonly to: string;
  readonly key: string;
  /** Only the four primary destinations carry a mark — see PRIMARY_LINKS. */
  readonly Icon?: (p: { size?: number }) => React.ReactElement;
  /**
   * Absolute URL for a destination outside this app, built from the active
   * locale. Used by the operator manual, which lives on the marketing origin:
   * a router <Link> would try to navigate the dashboard to /help and 404.
   */
  readonly externalHref?: (locale: string) => string;
}
interface NavGroup {
  readonly key: string;
  readonly links: readonly NavLinkDef[];
  /**
   * Mark for the group TRIGGER, so the bar reads as one row of icon+label
   * controls rather than four marked links followed by three bare words.
   *
   * Only the trigger. The links INSIDE the panel stay text: those menus hold
   * seven to eleven short labels, which scan fine as words, and giving each a
   * bespoke glyph would mean inventing twenty-one marks — several for concepts
   * ('Segments', 'Spin wheel') that no icon says faster than the word does.
   */
  readonly Icon?: (props: { size?: number }) => React.ReactElement;
}

/** The daily job. Always visible, never behind a menu. */
const PRIMARY_LINKS: readonly NavLinkDef[] = [
  { to: '/queue', key: 'queue', Icon: CarIcon },
  { to: '/pos', key: 'pos', Icon: ReceiptIcon },
  { to: '/customers', key: 'customers', Icon: UserIcon },
  { to: '/reports', key: 'reports', Icon: StarIcon },
];

/** A cashier's whole job: the board and the till. */
const FLOOR_LINKS: readonly NavLinkDef[] = PRIMARY_LINKS.filter(
  (l) => l.to === '/queue' || l.to === '/pos',
);

const GROUPS: readonly NavGroup[] = [
  {
    key: 'groupLoyalty',
    Icon: GiftIcon,
    links: [
      { to: '/loyalty/settings', key: 'loyalty' },
      { to: '/loyalty/challenges', key: 'challenges' },
      { to: '/loyalty/achievements', key: 'achievements' },
      { to: '/loyalty/spin-wheel', key: 'spinWheel' },
      { to: '/loyalty/vouchers', key: 'vouchers' },
      { to: '/loyalty/tags', key: 'tags' },
      { to: '/loyalty/promotions', key: 'promotions' },
    ],
  },
  {
    key: 'groupMarketing',
    Icon: BellIcon,
    links: [
      { to: '/marketing', key: 'marketing' },
      { to: '/marketing/share', key: 'shareKit' },
      { to: '/marketing/segments', key: 'segments' },
      { to: '/marketing/journeys', key: 'journeys' },
      { to: '/reviews', key: 'reviews' },
    ],
  },
  {
    key: 'groupSetup',
    Icon: BuildingIcon,
    links: [
      { to: '/catalog', key: 'catalog' },
      { to: '/products', key: 'products' },
      { to: '/outlets', key: 'outlets' },
      { to: '/staff', key: 'staff' },
      { to: '/branding/hours', key: 'hours' },
      { to: '/billing', key: 'billing' },
      { to: '/refunds', key: 'refunds' },
      { to: '/map', key: 'map' },
      { to: '/qr-print', key: 'printQr' },
      { to: '/academy', key: 'academy' },
      {
        // The manual is 14 articles x 4 locales on the marketing site. Sending
        // an operator to the version in THEIR language matters more here than
        // anywhere else in the product: someone reaching for help is already
        // stuck, and a wall of English is where they give up.
        to: '/help',
        key: 'help',
        externalHref: (locale) => `${env.marketingOrigin}/${locale}/help`,
      },
    ],
  },
];

const SUPERADMIN_GROUP: NavGroup = {
  key: 'groupAdmin',
  Icon: ShieldIcon,
  links: [
    { to: '/superadmin/finance', key: 'finance' },
    { to: '/superadmin/tenants', key: 'operators' },
    { to: '/superadmin/plans', key: 'plans' },
    { to: '/superadmin/payments', key: 'payments' },
    { to: '/superadmin/agents', key: 'agents' },
    { to: '/superadmin/recruiters', key: 'recruiters' },
    { to: '/superadmin/map', key: 'networkMap' },
    { to: '/superadmin/directory', key: 'directory' },
    { to: '/superadmin/setup', key: 'setup' },
    { to: '/superadmin/integrations', key: 'integrations' },
    { to: '/superadmin/ai', key: 'ai' },
  ],
};

function useIsSuperAdmin(): boolean {
  const { claims } = useAuth();
  const brand = usePlatformBrand();
  const superadmin = claims?.['superadmin'];
  return (
    typeof superadmin === 'object' &&
    superadmin !== null &&
    (superadmin as Record<string, unknown>)[brand.id] === true
  );
}

/**
 * Hide what an operator has not grown into yet (gap G20).
 *
 * A new operator met twenty-four capabilities at once — spin wheels, journeys,
 * segments — before washing a car. What survives filtering is everything they
 * can currently use, plus everything they are currently looking at.
 *
 * The current route is force-included for a reason that is easy to miss: an
 * operator can reach a staged screen by bookmark or by a link in an email, and
 * a nav that omits the page you are standing on reads as broken rather than
 * tidy.
 */
function useVisibleGroups(
  groups: readonly NavGroup[],
  stage: DisclosureStage,
  pathname: string,
): readonly NavGroup[] {
  return useMemo(() => {
    const onNow = (link: NavLinkDef) => pathname === link.to || pathname.startsWith(link.to + '/');
    return (
      groups
        .map((g) => ({
          ...g,
          links: g.links.filter((l) => isUnlocked(l.key, stage) || onNow(l)),
        }))
        // A group whose every link is staged away should not leave an empty menu
        // button behind.
        .filter((g) => g.links.length > 0)
    );
  }, [groups, stage, pathname]);
}

/**
 * How much of the nav row can carry words, by viewport.
 *
 * The row is never allowed to wrap or scroll. Wrapping is what produced the
 * header the owner rejected on a tablet: the bar overflowed, the browser
 * pushed whatever did not fit onto a line of its own, and the assistant button
 * ended up alone on a row of empty space. Scrolling is not an option either —
 * the group menus drop DOWN from this row, and an overflow container clips
 * them. So the row FITS, by shedding words in a fixed order: group labels
 * first (each menu keeps its icon, its name moves to the tooltip and the
 * accessible label), then the labels of the daily tabs you are not on.
 *
 * Thresholds are measured, not guessed: four labelled tabs ≈ 570px, four
 * labelled menus ≈ 480px (the super-admin's fifth ≈ 130px more), an icon-only
 * menu ≈ 64px, all inside the bar's 2 × 20px padding.
 */
type NavDensity = 'full' | 'iconMenus' | 'iconTabs';

function useNavDensity(): NavDensity {
  const belowFull = useIsCompact(1219);
  const belowMid = useIsCompact(919);
  return belowMid ? 'iconTabs' : belowFull ? 'iconMenus' : 'full';
}

/**
 * The navigation row: the four daily destinations and the grouped menus.
 * Rendered by the shell bar as its own tier, below the brand + utility row,
 * and only above the compact breakpoint — below it the drawer in
 * `AppNavUtilities` carries the same destinations.
 */
export function AppNavLinks() {
  const { t, locale } = useI18n();
  const { pathname } = useLocation();
  const isSuperAdmin = useIsSuperAdmin();
  const stage = useDisclosureStage();
  const isCompact = useIsCompact(720);
  const density = useNavDensity();
  const role = useMyRole();
  const floor = isFloorRole(role);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const allGroups = isSuperAdmin ? [...GROUPS, SUPERADMIN_GROUP] : GROUPS;
  // Super-admin tooling is internal and never staged — it is not part of an
  // operator's learning curve.
  const groups = useVisibleGroups(allGroups, isSuperAdmin ? 'growing' : stage, pathname);

  // Close any open menu on navigation, and on Escape, so none ever strands
  // open over the content.
  useEffect(() => setOpenGroup(null), [pathname]);
  useEffect(() => {
    if (openGroup === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenGroup(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openGroup]);

  if (isCompact || role === 'washer') return null;
  // A cashier works two screens. Showing them twenty destinations they cannot
  // open (the rules deny them) is noise to someone who may not read the labels.
  const primary = role === 'cashier' ? FLOOR_LINKS : PRIMARY_LINKS;
  return (
    <div style={navRowStyle}>
      <nav aria-label={t('dashboard.nav.primary')} style={segmentedStyle}>
        {primary.map((l) => (
          <PrimaryTab
            key={l.to}
            to={l.to}
            label={t(`dashboard.nav.${l.key}`)}
            iconOnlyWhenInactive={density === 'iconTabs'}
            {...(l.Icon ? { Icon: l.Icon } : {})}
          />
        ))}
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
        {(floor ? [] : groups).map((g) => (
          <GroupMenu
            key={g.key}
            group={g}
            label={t(`dashboard.nav.${g.key}`)}
            iconOnly={density !== 'full'}
            open={openGroup === g.key}
            onToggle={() => setOpenGroup((cur) => (cur === g.key ? null : g.key))}
            onClose={() => setOpenGroup(null)}
            t={t}
            accent={g.key === 'groupAdmin'}
            locale={locale}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The utility cluster at the right of the top row: appearance, language,
 * notifications, the assistant — and, below the compact breakpoint, the menu
 * button whose drawer carries every destination.
 *
 * The assistant is an icon button the size of the bell, not a labelled pill.
 * The pill was 217–340px depending on the brand name and the locale, which is
 * what made the old single row overflow at tablet widths; a fixed 44px circle
 * makes the cluster's width predictable at every brand and every language. Its
 * name survives as the tooltip and the accessible label.
 *
 * On a phone the appearance control moves into the drawer: it is a set-once
 * preference, and the 360px top row is better spent on the controls used
 * every shift.
 */
export function AppNavUtilities() {
  const { t, locale } = useI18n();
  const { pathname } = useLocation();
  const isSuperAdmin = useIsSuperAdmin();
  const stage = useDisclosureStage();
  const isCompact = useIsCompact(720);
  const role = useMyRole();
  const floor = isFloorRole(role);
  const [navOpen, setNavOpen] = useState(false);

  const allGroups = isSuperAdmin ? [...GROUPS, SUPERADMIN_GROUP] : GROUPS;
  const groups = useVisibleGroups(allGroups, isSuperAdmin ? 'growing' : stage, pathname);

  useEffect(() => setNavOpen(false), [pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div style={utilityClusterStyle}>
      {/* Washers and cashiers: language only. The appearance switch, the
          business assistant and the inbox are an owner's tools; on a washer's
          phone they pushed the cars below the fold (measured on an Android
          emulator, 2026-10-16). A cashier on a phone still gets the menu, with
          just the two screens they work. */}
      {isCompact || floor ? null : <ThemeToggle compact labels={appearanceLabels(t)} />}
      <LocaleSwitcher />
      {floor ? null : <InboxBell />}
      {floor ? null : <AskKilat />}
      {isCompact && role !== 'washer' ? (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            aria-label={t('dashboard.nav.menu')}
            aria-expanded={navOpen}
            aria-controls="app-nav-drawer"
            style={hamburgerStyle}
          >
            <HamburgerIcon open={navOpen} />
          </button>

          {navOpen ? (
            <>
              <div
                onClick={() => setNavOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 59 }}
                aria-hidden
              />
              <nav id="app-nav-drawer" aria-label={t('dashboard.nav.primary')} style={drawerStyle}>
                {(floor ? FLOOR_LINKS : PRIMARY_LINKS).map((l) => (
                  <DrawerLink key={l.to} to={l.to} label={t(`dashboard.nav.${l.key}`)} />
                ))}
                {(floor ? [] : groups).map((g) => (
                  <div key={g.key}>
                    <p style={drawerGroupLabelStyle}>{t(`dashboard.nav.${g.key}`)}</p>
                    {g.links.map((l) => (
                      <DrawerLink
                        key={l.to}
                        to={l.to}
                        label={t(`dashboard.nav.${l.key}`)}
                        superadmin={g.key === 'groupAdmin'}
                        {...(l.externalHref ? { href: l.externalHref(locale) } : {})}
                      />
                    ))}
                  </div>
                ))}
                <div style={drawerAppearanceStyle}>
                  <p style={drawerGroupLabelStyle}>{t('common.appearance.group')}</p>
                  <div style={{ padding: '0 0.875em' }}>
                    <ThemeToggle compact labels={appearanceLabels(t)} />
                  </div>
                </div>
              </nav>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** One of the four daily destinations. Filled when active — findable at a glance. */
function PrimaryTab({
  to,
  label,
  Icon,
  iconOnlyWhenInactive = false,
}: {
  readonly to: string;
  readonly label: string;
  readonly Icon?: (p: { size?: number }) => React.ReactElement;
  /** Narrowest tier: tabs you are not on show just their mark; where you ARE stays named. */
  readonly iconOnlyWhenInactive?: boolean;
}) {
  return (
    <NavLink
      to={to}
      aria-label={label}
      title={iconOnlyWhenInactive ? label : undefined}
      style={({ isActive }) => ({
        ...primaryTabStyle,
        ...(iconOnlyWhenInactive && !isActive && Icon ? iconOnlyTabStyle : null),
        ...(isActive ? primaryTabActiveStyle : null),
      })}
    >
      {({ isActive }) => (
        <>
          {Icon ? (
            <span aria-hidden style={{ display: 'inline-flex', opacity: 0.85 }}>
              <Icon size={15} />
            </span>
          ) : null}
          {iconOnlyWhenInactive && !isActive && Icon ? null : label}
        </>
      )}
    </NavLink>
  );
}

function GroupMenu({
  group,
  label,
  open,
  onToggle,
  onClose,
  t,
  accent,
  locale,
  iconOnly = false,
}: {
  readonly group: NavGroup;
  readonly label: string;
  /** Show the mark and chevron only; the name moves to the tooltip + accessible label. */
  readonly iconOnly?: boolean;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onClose: () => void;
  readonly t: (k: string) => string;
  readonly accent?: boolean;
  /** Active locale, so the manual link lands on the operator's own language. */
  readonly locale: string;
}) {
  const { pathname } = useLocation();
  const containsCurrent = group.links.some((l) => pathname.startsWith(l.to));
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={label}
        title={iconOnly ? label : undefined}
        style={{
          ...groupTriggerStyle,
          ...(iconOnly && group.Icon ? { padding: '0 0.6em' } : null),
          ...(accent ? { color: 'var(--color-primary)' } : null),
          ...(containsCurrent || open ? groupTriggerActiveStyle : null),
        }}
      >
        {group.Icon ? <group.Icon size={15} /> : null}
        {iconOnly && group.Icon ? null : label}
        <Chevron open={open} />
      </button>
      {open ? (
        <>
          <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 59 }} aria-hidden />
          <div style={menuPanelStyle}>
            {group.links.map((l) => (
              <DrawerLink
                key={l.to}
                to={l.to}
                label={t(`dashboard.nav.${l.key}`)}
                superadmin={accent}
                {...(l.externalHref ? { href: l.externalHref(locale) } : {})}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function DrawerLink({
  to,
  label,
  superadmin,
  href,
}: {
  readonly to: string;
  readonly label: string;
  readonly superadmin?: boolean;
  /** When present, render a real anchor — the destination is another origin. */
  readonly href?: string;
}) {
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        style={{
          ...drawerLinkStyle,
          color: superadmin ? 'var(--color-primary)' : 'var(--color-on-surface)',
          fontWeight: 500,
        }}
      >
        {label}
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        ...drawerLinkStyle,
        color: superadmin ? 'var(--color-primary)' : 'var(--color-on-surface)',
        background: isActive ? 'var(--color-primary-soft)' : 'transparent',
        fontWeight: isActive ? 600 : 500,
      })}
    >
      {label}
    </NavLink>
  );
}

function HamburgerIcon({ open }: { readonly open: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d={open ? 'M6 6 L18 18 M18 6 L6 18' : 'M4 7 H20 M4 12 H20 M4 17 H20'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function Chevron({ open }: { readonly open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      style={{
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 160ms var(--ease-out, ease)',
        flexShrink: 0,
        opacity: 0.65,
      }}
    >
      <path d="M6 9 L12 15 L18 9" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
}

const navRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  minWidth: 0,
  // Never wrap: see useNavDensity — the row sheds words instead.
  flexWrap: 'nowrap',
};

const utilityClusterStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flexWrap: 'nowrap',
  flexShrink: 0,
};

const iconOnlyTabStyle: React.CSSProperties = {
  padding: 0,
  width: 'calc(var(--touch-target) - 8px)',
  justifyContent: 'center',
};

const drawerAppearanceStyle: React.CSSProperties = {
  paddingBottom: 'var(--space-2)',
};

/** Segmented control: one connected surface, so the four read as a set. */
const segmentedStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: 3,
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-border)',
  minWidth: 0,
};

const primaryTabStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--color-on-surface-muted)',
  textDecoration: 'none',
  padding: '0 0.9em',
  borderRadius: 'var(--radius-pill)',
  minHeight: 'calc(var(--touch-target) - 8px)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.45em',
  whiteSpace: 'nowrap',
  transition: 'background 160ms ease, color 160ms ease',
};

const primaryTabActiveStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  boxShadow: '0 1px 2px var(--color-border-strong), 0 0 0 1px var(--tint-subtle)',
};

const groupTriggerStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4em',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  color: 'var(--color-on-surface-muted)',
  background: 'transparent',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-pill)',
  padding: '0 0.85em',
  minHeight: 'var(--touch-target)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 160ms ease, color 160ms ease, border-color 160ms ease',
};

const groupTriggerActiveStyle: React.CSSProperties = {
  color: 'var(--color-on-surface)',
  background: 'var(--tint-subtle)',
  borderColor: 'var(--color-border-strong)',
};

const menuPanelStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  right: 0,
  zIndex: 60,
  display: 'grid',
  gap: 2,
  padding: 'var(--space-2)',
  minWidth: 200,
  maxHeight: '70dvh',
  overflowY: 'auto',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-3)',
};

const hamburgerStyle: React.CSSProperties = {
  minWidth: 'var(--touch-target)',
  minHeight: 'var(--touch-target)',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

const drawerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  zIndex: 60,
  display: 'grid',
  gap: 'var(--space-1)',
  padding: 'var(--space-2)',
  minWidth: 240,
  maxWidth: 'calc(100vw - 2 * var(--space-4))',
  maxHeight: '70dvh',
  overflowY: 'auto',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-3)',
};

const drawerLinkStyle: React.CSSProperties = {
  padding: '0 0.875em',
  minHeight: 'var(--touch-target)',
  display: 'flex',
  alignItems: 'center',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-body)',
  lineHeight: 1.35,
  textDecoration: 'none',
};

const drawerGroupLabelStyle: React.CSSProperties = {
  margin: 'var(--space-2) 0.875em var(--space-1) 0.875em',
  paddingTop: 'var(--space-2)',
  borderTop: '1px solid var(--color-border)',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};
